import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import type { CreateAgentRequestDTO } from '@conversed/shared/src/central/dto/agent.dto';
import type { AgentData } from '@conversed/shared/src/central/types/agent.type';
import { useInboxes } from '@hooks/settings/inboxes/useInboxes';
import type React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useModal } from '../../../../hooks/modal/useModal';

type Props = {
  agentData?: Partial<CreateAgentRequestDTO>;
  onSubmit: (data: CreateAgentRequestDTO) => void;
};

export const AgentDataModal: React.FC<Props> = ({ agentData, onSubmit }) => {
  const { t } = useTranslation();
  const { inboxes } = useInboxes();
  const { handleSubmit, control } = useForm<AgentData>({
    shouldUseNativeValidation: true,
    defaultValues: agentData,
  });

  const { hideModal } = useModal();

  const hideAndSubmit = (data: AgentData) => {
    onSubmit(data);
    hideModal();
  };

  return (
    <form
      className='tailwind-modal-form'
      onSubmit={handleSubmit(hideAndSubmit)}>
      <div className='tailwind-modal-body'>
        <div className='mt-3 grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-2'>
          <div className='md:col-span-1'>
            <InputWithLabelT
              field='firstName'
              placeholder={t('settings.agent.first-name')}
              label={t('settings.agent.first-name')}
              id='first-name'
              control={control}
              options={{
                required: t('settings.agent.validation.first-name'),
                minLength: 3,
              }}
            />
          </div>

          <div className='md:col-span-1'>
            <InputWithLabelT
              field='lastName'
              placeholder={t('settings.agent.last-name')}
              label={t('settings.agent.last-name')}
              id='last-name'
              control={control}
              options={{
                required: t('settings.agent.validation.last-name'),
                minLength: 3,
              }}
            />
          </div>
        </div>

        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          <div className='col-span-1'>
            <InputWithLabelT
              type='email'
              field='email'
              placeholder={t('settings.agent.email')}
              label={t('settings.agent.email')}
              id='email'
              control={control}
              options={{
                required: t('general.validation.required'),
              }}
            />
          </div>
        </div>

        <div className='col-span-1 mt-4'>
          <LabelT text={t('settings.agent.inboxes')} />
          {inboxes && inboxes.length > 0 ? (
            <Controller
              name='inboxes'
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4 flex items-center justify-start font-light text-sm my-3 gap-2 select-none appearance-none relative'>
                  {inboxes.map((inbox) => (
                    <div key={inbox.identifier} className='flex items-center'>
                      <input
                        className='cursor-pointer'
                        type='checkbox'
                        id={`inbox-${inbox.identifier}`}
                        value={inbox.identifier}
                        checked={field?.value?.includes(inbox.identifier)}
                        onChange={(e) => {
                          const newValue = e.target.checked
                            ? [...(field?.value || []), inbox.identifier]
                            : field?.value?.filter(
                                (id: string) => id !== inbox.identifier,
                              );
                          field.onChange(newValue);
                        }}
                      />
                      <label
                        htmlFor={`inbox-${inbox.identifier}`}
                        className='pl-2 cursor-pointer'>
                        {inbox.name}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            />
          ) : (
            <p className='flex items-center text-xs italic gap-1'>
              <MaterialIcon
                icon='warning'
                classes={['text-yellow-base text-lg']}
              />
              <span>{t('settings.agent.no-inboxes')}</span>
            </p>
          )}
        </div>
      </div>
      <div className='tailwind-modal-footer mt-8'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </form>
  );
};

export default AgentDataModal;
